/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import LazyBackground from "@eisinc/App/Components/LazyBackground/LazyBackground";
import styles from "@eisinc/App/Widgets/InfoCard/InfoCard.style";
import ArrowGo from "@eisinc/XDL/Icons/ArrowGo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import React, { useState } from "react";

const enum fields {
    imageUrl = "imageUrl",
    link = "link",
    linkText = "linkText",
    copy = "copy",
    openInNewTab = "openInNewTab",
}

interface InfoCardProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.copy]: string;
        [fields.linkText]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
    };
}

type Props = InfoCardProps;

const InfoCardTemplateWidget: React.FC<Props> = ({ fields }) => {
    const image = <LazyBackground classNames="bg-image" src={fields.imageUrl} />;

    const url = useGetLink(fields.link)?.url;

    return (
        <Link
            href={url}
            target={fields.openInNewTab ? "_blank" : undefined}
            color="text.main"
            hoverStyle={styles.linkHoverStyles}
            css={styles.wrapperCss}
            aria-label={fields.linkText}
        >
            {image}
            <StyledWrapper css={styles.textContentWrapperCss}>
                <Typography variant="h6" css={styles.headingCss} size="18px" lineHeight="22px">
                    {fields.linkText}
                </Typography>
                <StyledWrapper css={styles.textContentDescriptionCss}>
                    {fields.copy && (
                        <Typography variant="p" size="14px" lineHeight="20px" css={styles.copyCss}>
                            {fields.copy}
                        </Typography>
                    )}
                    <StyledWrapper css={styles.iconWrapperCss}>
                        <Icon src={ArrowGo} title={fields.linkText} color="primary.main" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </Link>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.imageUrl,
            fieldType: "General",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.link,
            displayName: "Link",
            editorTemplate: "LinkField",
            defaultValue: { value: "", type: "Page" },
            fieldType: "General",
            isRequired: true,
            validate: (link: LinkFieldValue) => {
                if (!link || !link.value) {
                    return "A value must be provided.";
                }
                return null;
            },
        },
        {
            name: fields.linkText,
            displayName: "Link Label",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
            validate: (value: string | null) => {
                if (value && value.length > 40) {
                    return "Max length is 40 characters";
                }
                return null;
            },
        },
        {
            name: fields.openInNewTab,
            displayName: "Open in New Tab",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.copy,
            displayName: "Copy",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            validate: (value: string | null) => {
                if (value && value.length > 200) {
                    return "Max length is 200 characters";
                }
                return null;
            },
        },
    ],
};

const widgetModule: WidgetModule = {
    component: InfoCardTemplateWidget,
    definition,
};

export default widgetModule;
